<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">

    <div class="filter my-3">
      <div class="filter-head">
        <h4>واتساب — {{ group?.name ?? '...' }}</h4>
        <div class="buttons">
          <router-link :to="`/grades/${gradeSlug}/groups/${groupId}`" class="btn btn-outline-primary">
            <i class="pi pi-arrow-right me-1"></i> رجوع لطلاب المجموعة
          </router-link>
        </div>
      </div>
    </div>

    <!-- ── Group linking ── -->
    <div class="card p-3 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <strong>حالة الربط: </strong>
          <span v-if="group?.whatsapp_linked" class="status-badge status-badge--accepted">
            مرتبطة — {{ group.whatsapp_group_name }}
          </span>
          <span v-else class="status-badge status-badge--pending">غير مرتبطة بعد</span>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" :disabled="syncing" @click="syncGroups">
            <i class="pi pi-spin pi-spinner" v-if="syncing"></i>
            <i class="pi pi-refresh me-1" v-else></i> مزامنة مجموعات الواتساب
          </button>
          <button class="btn PrimaryButton" :disabled="group?.whatsapp_linked || creating" @click="createWhatsAppGroup">
            <i class="pi pi-spin pi-spinner-dotted" v-if="creating"></i>
            {{ creating ? 'جاري الإنشاء...' : 'إنشاء مجموعة واتساب' }}
          </button>
          <button class="btn btn-success" v-if="showResumeButton" :disabled="resumeDisabled" @click="resumeAddingMembers">
            <i class="pi pi-spin pi-spinner-dotted" v-if="resuming"></i>
            <i class="pi pi-play me-1" v-else></i>
            {{ resuming ? 'جاري الاستكمال...' : '▶ استكمال إضافة الأعضاء' }}
          </button>
        </div>
      </div>

      <div class="mt-3" v-if="queueStatus && queueStatus.total">
        <div class="d-flex justify-content-between mb-1">
          <span>{{ queueStatus.added }} / {{ queueStatus.total }} عضو تم إضافتهم</span>
          <span class="text-muted">المتبقي: {{ queueStatus.remaining }}</span>
        </div>
        <div class="progress" style="height: 8px;">
          <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: queueStatus.progress + '%' }"
            :aria-valuenow="queueStatus.progress"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    </div>

    <!-- ── Send to group ── -->
    <div class="card p-3 mb-4" v-if="group?.whatsapp_linked">
      <h5 class="mb-3">إرسال رسالة إلى المجموعة</h5>
      <div class="form-group mb-2">
        <textarea class="form-control" rows="3" v-model="groupMessage" placeholder="اكتب الرسالة هنا"></textarea>
      </div>
      <div class="row g-2 align-items-end">
        <div class="col-md-4 col-12">
          <label>نوع الوسائط (اختياري)</label>
          <select class="form-control" v-model="groupMediaType">
            <option :value="null">بدون وسائط</option>
            <option value="image">صورة</option>
            <option value="document">مستند</option>
            <option value="video">فيديو</option>
            <option value="audio">صوت</option>
            <option value="voice">رسالة صوتية</option>
          </select>
        </div>
        <div class="col-md-5 col-12" v-if="groupMediaType">
          <label>الملف</label>
          <input type="file" class="form-control" @change="e => groupMediaFile = e.target.files[0]" />
        </div>
        <div class="col-md-3 col-12">
          <button class="btn PrimaryButton w-100" :disabled="sendingGroup" @click="sendGroupMessage">
            <i class="pi pi-spin pi-spinner-dotted" v-if="sendingGroup"></i>
            {{ sendingGroup ? 'جاري الإرسال...' : 'إرسال للمجموعة' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Send direct message ── -->
    <div class="card p-3 mb-4">
      <h5 class="mb-3">إرسال رسالة مباشرة لأولياء الأمور</h5>

      <div class="row g-2 mb-3">
        <div class="col-md-8 col-12 d-flex flex-wrap gap-2">
          <button class="btn btn-sm" :class="recipientMode==='all' ? 'btn-primary' : 'btn-outline-primary'" @click="applyRecipientMode('all')">الكل</button>
          <button class="btn btn-sm" :class="recipientMode==='accepted' ? 'btn-success' : 'btn-outline-success'" @click="applyRecipientMode('accepted')">المقبولون فقط</button>
          <button class="btn btn-sm" :class="recipientMode==='pending' ? 'btn-warning text-white' : 'btn-outline-warning'" @click="applyRecipientMode('pending')">المعلقون فقط</button>
          <button class="btn btn-sm" :class="recipientMode==='rejected' ? 'btn-danger' : 'btn-outline-danger'" @click="applyRecipientMode('rejected')">المرفوضون فقط</button>
        </div>
        <div class="col-md-4 col-12 text-md-end">
          <span class="text-muted">تم اختيار {{ selectedIds.length }} من {{ recipientCandidates.length }}</span>
        </div>
      </div>

      <div class="recipients-list mb-3">
        <div v-if="loadingStudents" class="text-center py-3">
          <i class="pi pi-spin pi-spinner"></i>
        </div>
        <div v-else-if="recipientCandidates.length === 0" class="text-muted py-2">لا يوجد طلاب لديهم رقم هاتف ولي أمر صالح</div>
        <div v-else class="row">
          <div class="col-md-4 col-6" v-for="s in recipientCandidates" :key="s.id">
            <label class="d-flex align-items-center gap-2">
              <input type="checkbox" :checked="selectedIds.includes(s.id)" @change="toggleStudent(s.id)" />
              <span>{{ s.name }} — {{ s.parent_phone }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="form-group mb-2">
        <textarea class="form-control" rows="3" v-model="directMessage" placeholder="اكتب الرسالة هنا"></textarea>
      </div>
      <div class="row g-2 align-items-end">
        <div class="col-md-4 col-12">
          <label>نوع الوسائط (اختياري)</label>
          <select class="form-control" v-model="directMediaType">
            <option :value="null">بدون وسائط</option>
            <option value="image">صورة</option>
            <option value="document">مستند</option>
            <option value="video">فيديو</option>
            <option value="audio">صوت</option>
            <option value="voice">رسالة صوتية</option>
          </select>
        </div>
        <div class="col-md-5 col-12" v-if="directMediaType">
          <label>الملف</label>
          <input type="file" class="form-control" @change="e => directMediaFile = e.target.files[0]" />
        </div>
        <div class="col-md-3 col-12">
          <button class="btn PrimaryButton w-100" :disabled="sendingDirect || selectedIds.length === 0" @click="sendDirectMessage">
            <i class="pi pi-spin pi-spinner-dotted" v-if="sendingDirect"></i>
            {{ sendingDirect ? 'جاري الإرسال...' : `إرسال مباشر (${selectedIds.length})` }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── History ── -->
    <div class="card p-3">
      <h5 class="mb-3">سجل الرسائل</h5>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>النوع</th>
            <th>الرسالة</th>
            <th>الوسائط</th>
            <th>الحالة</th>
            <th>التاريخ</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loadingHistory">
            <td colspan="6" class="text-center py-3"><i class="pi pi-spin pi-spinner"></i></td>
          </tr>
          <tr v-else-if="history.length === 0">
            <td colspan="6" class="text-center py-3 text-muted">لا يوجد سجل رسائل بعد</td>
          </tr>
          <tr v-for="(m, i) in history" :key="m.id" v-else>
            <td>{{ i + 1 }}</td>
            <td>{{ m.type === 'group' ? 'مجموعة' : 'مباشر' }}</td>
            <td>{{ (m.message || '').slice(0, 60) }}</td>
            <td>
              <a v-if="m.media" :href="m.media" target="_blank">{{ m.media_type }}</a>
              <span v-else>—</span>
            </td>
            <td>
              <span v-if="m.status === 'sent'" class="status-badge status-badge--accepted">تم الإرسال</span>
              <span v-else-if="m.status === 'failed'" class="status-badge status-badge--rejected">فشل</span>
              <span v-else class="status-badge status-badge--pending">قيد الإرسال</span>
            </td>
            <td>{{ formatDate(m.created_at) }}</td>
          </tr>
        </tbody>
      </table>
      <pagination v-if="historyPaginator" :data="historyPaginator" class="mx-auto d-flex align-items-center justify-content-center pagination" @pagination-change-page="fetchHistory"></pagination>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';

export default {
  name: 'GradeGroupWhatsApp',
  components: { pagination, Breadcrumb },

  data() {
    return {
      group: null,
      students: [],
      loadingStudents: true,

      recipientMode: 'all',
      selectedIds: [],

      groupMessage: '',
      groupMediaType: null,
      groupMediaFile: null,
      sendingGroup: false,

      directMessage: '',
      directMediaType: null,
      directMediaFile: null,
      sendingDirect: false,

      creating: false,
      syncing: false,

      resuming: false,
      queueStatus: null,
      queuePollTimer: null,

      history: [],
      historyPaginator: null,
      loadingHistory: true,
    };
  },

  computed: {
    gradeSlug() { return this.$route.params.gradeSlug; },
    groupId() { return this.$route.params.groupId; },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'مجموعات الطلاب', path: `/grades/${this.gradeSlug}/groups` },
        { name: this.group?.name ?? 'المجموعة', path: `/grades/${this.gradeSlug}/groups/${this.groupId}` },
        { name: 'واتساب', path: `/grades/${this.gradeSlug}/groups/${this.groupId}/whatsapp` },
      ];
    },
    recipientCandidates() {
      return this.students.filter(s => s.parent_phone);
    },
    // Shows whenever there's a linked group and no queue actively running or
    // already finished. Deliberately wider than just the three paused
    // statuses: with Recovery Mode, clicking Resume with NO persisted queue
    // at all (queueStatus null/'idle') is exactly how the button now offers
    // to reconcile DB vs actual WhatsApp membership — hiding it in that case
    // would make Recovery Mode unreachable from the UI. 'pending' still
    // counts as "don't need the button" since it's already waiting
    // automatically behind another active queue on the same instance.
    showResumeButton() {
      return !!this.group?.whatsapp_linked
        && !['processing', 'pending', 'completed'].includes(this.queueStatus?.status);
    },
    resumeDisabled() {
      return this.resuming || this.queueStatus?.status === 'processing';
    },
  },

  methods: {
    formatDate(d) {
      return d ? new Date(d).toLocaleString('ar-EG') : '';
    },

    async fetchGroup() {
      axios.defaults.headers.common['per_page'] = 1000;
      const { data } = await axios.get(`/student-groups/${this.groupId}`);
      this.group = data.data.group;
      this.students = data.data.students?.data ?? [];
      this.loadingStudents = false;
      this.applyRecipientMode(this.recipientMode);
    },

    applyRecipientMode(mode) {
      this.recipientMode = mode;
      if (mode === 'all') {
        this.selectedIds = this.recipientCandidates.map(s => s.id);
      } else {
        const statusMap = { accepted: 1, pending: 0, rejected: 2 };
        this.selectedIds = this.recipientCandidates
          .filter(s => s.reservation_status === statusMap[mode])
          .map(s => s.id);
      }
    },

    toggleStudent(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },

    async createWhatsAppGroup() {
      this.creating = true;
      try {
        const { data } = await axios.post(`/student-groups/${this.groupId}/whatsapp/create`);
        if (data.status) {
          Swal.fire('', data.message, 'success');
          this.group = data.data;
        } else {
          Swal.fire('', data.message, 'error');
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'حدث خطأ', 'error');
      } finally {
        this.creating = false;
      }
    },

    async syncGroups() {
      this.syncing = true;
      try {
        const { data } = await axios.post('/student-groups/whatsapp/sync');
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        await this.fetchGroup();
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'حدث خطأ', 'error');
      } finally {
        this.syncing = false;
      }
    },

    // Silent by design — this runs on every poll tick and must never spam
    // the user with error toasts for a transient/absent queue.
    async fetchQueueStatus() {
      try {
        const { data } = await axios.get(`/student-groups/${this.groupId}/whatsapp/queue-status`);
        this.queueStatus = data.data || null;
      } catch {
        this.queueStatus = null;
      }
    },

    // Same button, same click handler — the backend decides internally
    // whether a persisted queue exists to resume, or whether to reconcile DB
    // vs actual WhatsApp membership (Recovery Mode). Recovery Mode returns a
    // `needs_confirmation` preview first (starts nothing) so we can show the
    // counts before committing; confirming re-calls the exact same endpoint
    // with `confirm: true`.
    async resumeAddingMembers() {
      this.resuming = true;
      try {
        const { data } = await axios.post(`/student-groups/${this.groupId}/whatsapp/resume`);

        if (data.data?.needs_confirmation) {
          const d = data.data;
          const confirmResult = await Swal.fire({
            title: 'تأكيد استكمال الإضافة',
            html: `أعضاء واتساب الحاليون: <strong>${d.current_members}</strong><br>` +
                  `الأعضاء المتوقعون: <strong>${d.expected_members}</strong><br>` +
                  `المتبقي إضافتهم: <strong>${d.remaining_count}</strong>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'نعم، استكمال',
            cancelButtonText: 'إلغاء',
          });

          if (!confirmResult.isConfirmed) {
            return;
          }

          const { data: confirmedData } = await axios.post(`/student-groups/${this.groupId}/whatsapp/resume`, { confirm: true });
          Swal.fire('', confirmedData.message, confirmedData.status ? 'success' : 'error');
          await this.fetchQueueStatus();
          return;
        }

        Swal.fire('', data.message, data.status ? 'success' : 'error');
        await this.fetchQueueStatus();
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'حدث خطأ', 'error');
      } finally {
        this.resuming = false;
      }
    },

    startQueuePolling() {
      this.stopQueuePolling();
      this.queuePollTimer = setInterval(async () => {
        if (!this.group?.whatsapp_linked) {
          this.stopQueuePolling();
          return;
        }
        await this.fetchQueueStatus();
        // Backend now always returns a populated status object (never null,
        // 'idle' instead of no-queue) — stop polling on either terminal state.
        if (!this.queueStatus || ['completed', 'idle'].includes(this.queueStatus.status)) {
          this.stopQueuePolling();
        }
      }, 8000);
    },

    stopQueuePolling() {
      if (this.queuePollTimer) {
        clearInterval(this.queuePollTimer);
        this.queuePollTimer = null;
      }
    },

    async sendGroupMessage() {
      this.sendingGroup = true;
      try {
        const fd = new FormData();
        fd.append('message', this.groupMessage || '');
        if (this.groupMediaType) fd.append('media_type', this.groupMediaType);
        if (this.groupMediaFile) fd.append('media', this.groupMediaFile);

        const { data } = await axios.post(`/student-groups/${this.groupId}/whatsapp/send-group`, fd);
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        if (data.status) {
          this.groupMessage = '';
          this.groupMediaFile = null;
          this.groupMediaType = null;
          this.fetchHistory();
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'فشل الإرسال', 'error');
      } finally {
        this.sendingGroup = false;
      }
    },

    async sendDirectMessage() {
      this.sendingDirect = true;
      try {
        const fd = new FormData();
        fd.append('message', this.directMessage || '');
        fd.append('recipient_mode', 'manual');
        this.selectedIds.forEach(id => fd.append('student_ids[]', id));
        if (this.directMediaType) fd.append('media_type', this.directMediaType);
        if (this.directMediaFile) fd.append('media', this.directMediaFile);

        const { data } = await axios.post(`/student-groups/${this.groupId}/whatsapp/send-direct`, fd);
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        if (data.status) {
          this.directMessage = '';
          this.directMediaFile = null;
          this.directMediaType = null;
          this.fetchHistory();
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'فشل الإرسال', 'error');
      } finally {
        this.sendingDirect = false;
      }
    },

    async fetchHistory(page = 1) {
      this.loadingHistory = true;
      try {
        axios.defaults.headers.common['per_page'] = 10;
        const { data } = await axios.get(`/student-groups/${this.groupId}/whatsapp/history`, { params: { page } });
        this.historyPaginator = data.data;
        this.history = data.data.data ?? [];
      } catch {
        this.history = [];
      } finally {
        this.loadingHistory = false;
      }
    },
  },

  async mounted() {
    await this.fetchGroup();
    this.fetchHistory();
    if (this.group?.whatsapp_linked) {
      await this.fetchQueueStatus();
      this.startQueuePolling();
    }
  },

  beforeUnmount() {
    this.stopQueuePolling();
  },
};
</script>

<style scoped>
.recipients-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0.75rem;
}
</style>
